import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { QueryParamsCard } from '../../Prompts/components/PrompPreview/QueryParamsCard';

import { QueryTester } from './QueryTester';

import { FormGroup } from '@/components/FormGroup';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { useCreatePromptVariable } from '@/hooks/useCreatePromptVariable';
import { useTestQuery } from '@/hooks/useTestQuery';
import { FilterPromptVariablesDto } from '@/services/PromptsService';

const promptVariableSchema = z.object({
  name: z.string().trim().min(1, 'Variable name is required'),
  query: z.string().trim().min(1, 'Query is required'),
});

type PromptVariableFormData = z.infer<typeof promptVariableSchema>;

interface AddPromptVariableFormProps {
  open: boolean;
  onClose: () => void;
  filter: FilterPromptVariablesDto;
}

export function AddPromptVariableForm({
  open,
  onClose,
  filter,
}: AddPromptVariableFormProps) {
  const [queryParams, setQueryParams] = useState<
    Record<string, string | number | boolean>
  >({});

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<PromptVariableFormData>({
    resolver: zodResolver(promptVariableSchema),
    defaultValues: {
      name: '',
      query: '',
    },
  });

  const { createPromptVariable, isLoading } = useCreatePromptVariable(filter);
  const { testQuery, isTestingQuery } = useTestQuery();

  const queryValue = watch('query');

  async function onSubmit(data: PromptVariableFormData) {
    try {
      const sanitizedData = {
        name: data.name.replace(/\s+/g, ''),
        query: data.query,
      };
      await createPromptVariable(sanitizedData);

      toast.success(
        'Prompt variable created successfully! Any spaces have been removed from the variable name.',
      );
      reset();
      setQueryParams({});
      onClose();
    } catch (error) {
      toast.error(
        error instanceof AxiosError
          ? error.response?.data?.message
          : 'Failed to create prompt variable. Please try again.',
      );
    }
  }

  const handlePayload = useCallback(() => {
    // Não faz nada - só para satisfazer a interface
  }, []);
  function handleClose() {
    setQueryParams({});
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Prompt Variable</DialogTitle>
          <DialogDescription>
            Fill in the details for the new prompt variable.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid gap-6 py-4">
            <FormGroup error={{ message: errors.name?.message, font: 'sm' }}>
              <RequiredLabel htmlFor="name">Variable Name</RequiredLabel>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Enter variable name without spaces..."
                  />
                )}
              />
            </FormGroup>

            <FormGroup error={{ message: errors.query?.message, font: 'sm' }}>
              <RequiredLabel htmlFor="query">Query</RequiredLabel>
              <Controller
                name="query"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    rows={6}
                    placeholder="Enter your SQL query or data retrieval logic here... Use {{paramName}} for parameters."
                  />
                )}
              />
              <p className="mt-1 text-sm text-gray-500">
                Define how this variable should be populated. Use{' '}
                {`{{paramName}}`} for query parameters.
              </p>
            </FormGroup>

            {queryValue && (
              <QueryParamsCard
                onPayload={handlePayload}
                queryParams={queryParams}
                onQueryParamsChange={setQueryParams}
                query={queryValue}
              />
            )}

            <QueryTester
              query={queryValue}
              queryParams={queryParams}
              isTesting={isTestingQuery}
              onTest={testQuery}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create Variable'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
